import { Crown, Sparkles, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { useSubscription, SubscriptionPlan } from "@/hooks/useSubscription";
import { Skeleton } from "./ui/skeleton";

interface PlanBadgeProps {
  showUpgrade?: boolean;
  className?: string;
}

export function PlanBadge({ showUpgrade = true, className }: PlanBadgeProps) {
  const { plan, isLoading, isActive, cancelAtPeriodEnd, daysUntilExpiration } = useSubscription();

  if (isLoading) {
    return <Skeleton className="h-8 w-24 rounded-full" />;
  }

  const planConfig: Record<SubscriptionPlan, {
    label: string;
    icon: typeof Zap;
    bgClass: string;
    textClass: string;
    borderClass: string;
  }> = {
    free: {
      label: "Gratuit",
      icon: Zap,
      bgClass: "bg-muted",
      textClass: "text-muted-foreground",
      borderClass: "border-border",
    },
    pro: {
      label: "Pro",
      icon: Sparkles,
      bgClass: "bg-primary/20",
      textClass: "text-primary",
      borderClass: "border-primary/30",
    },
    business: {
      label: "Business",
      icon: Crown,
      bgClass: "bg-highlight/20",
      textClass: "text-highlight",
      borderClass: "border-highlight/30",
    },
  };

  const config = planConfig[plan];
  const Icon = config.icon;

  return (
    <div className={cn("space-y-2", className)}>
      <div
        className={cn(
          "inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-medium",
          config.bgClass,
          config.textClass,
          config.borderClass
        )}
      >
        <Icon className="w-4 h-4" />
        <span>Plan {config.label}</span>
      </div>
      
      {cancelAtPeriodEnd && daysUntilExpiration !== null && (
        <p className="text-xs text-muted-foreground">
          Expire dans {daysUntilExpiration} jours
        </p>
      )}
      
      {showUpgrade && plan === "free" && isActive && (
        <a 
          href="/pricing" 
          className="block text-xs text-primary hover:underline"
        >
          Passer à Pro →
        </a>
      )}
    </div>
  );
}
